/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {useEffect, useState} from 'react'
import canvasI18next from './index'

// i18n.language only moves on a real language switch. For a namespace in
// LAZY_ELIGIBLE_NAMESPACES, the production sequence is: boot as 'en', mount,
// t() returns the fallback, the bundle lands later on that SAME locale --
// i18n.language never changes, so a key built from it alone never catches
// the correction. This adds the one signal that does: the store's 'added'
// event, filtered to this namespace, plus the real language for a genuine
// switch. Use the returned string as (part of) a useFireOnceForKey key.
export function useNamespaceReadySignal(ns: string): string {
  const [signal, setSignal] = useState(() => `${canvasI18next.language}|0`)

  useEffect(() => {
    let addedCount = 0
    const onAdded = (_lng: string, addedNs: string) => {
      if (addedNs !== ns) return
      addedCount += 1
      setSignal(`${canvasI18next.language}|${addedCount}`)
    }
    const onLanguageChanged = () => {
      setSignal(`${canvasI18next.language}|${addedCount}`)
    }
    canvasI18next.store.on('added', onAdded)
    canvasI18next.on('languageChanged', onLanguageChanged)
    return () => {
      canvasI18next.store.off('added', onAdded)
      canvasI18next.off('languageChanged', onLanguageChanged)
    }
  }, [ns])

  return signal
}
